import React, { useEffect, useState } from 'react'
import BreadCrumb from '../../../Common/BreadCrumb';
import { Card, Col, Container, Row, Spinner, Alert } from 'react-bootstrap';
import {useTranslation} from "react-i18next";
import IrregularAreaCharts from "../../Components/Apexcharts/Area/IrregularAreaCharts";
import SimplePie from "../../Components/Apexcharts/Pie/SimplePie";
import Basicchart from "../../Components/Apexcharts/Bar/Basic";
import { dashboardAPI } from '../../../services/api';
import Chart from "react-apexcharts";

const SalesReport = () => {
    const {t} = useTranslation();
    document.title=t('SalesReports.title');
    
    const [stats, setStats] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Check if demo user
    const isDemoUser = () => {
        const user = sessionStorage.getItem("authUser");
        if (!user) return false;
        try {
            const userData = JSON.parse(user);
            const token = userData.token || userData.access_token;
            return token && typeof token === 'string' && token.startsWith('demo-token-');
        } catch {
            return false;
        }
    };

    useEffect(() => {
        const fetchStats = async () => {
            if (isDemoUser()) {
                setIsLoading(false);
                return; // Use mock data for demo users
            }
            
            setIsLoading(true);
            setError(null);
            try {
                const response: any = await dashboardAPI.getDashboardStats({});
                const data = response?.data || response;
                setStats(data);
            } catch (err: any) {
                console.error('Error fetching sales report:', err);
                setError(err?.message || t('Common.error_loading_data'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchStats();
    }, []);

    // Calculate average order value
    const averageOrderValue = stats && stats.totalTransactions && stats.totalTransactions > 0
        ? (Number(stats.totalRevenue || 0) / Number(stats.totalTransactions)).toFixed(2)
        : '0.00';

    // Get top store from transactionsByStore
    const topStore = stats?.transactionsByStore 
        ? Object.entries(stats.transactionsByStore)
            .sort(([,a]: any, [,b]: any) => b - a)[0]
        : null;

    // Prepare daily revenue trend data for chart
    const dailyRevenueData = stats?.dailyRevenueTrend 
        ? Object.entries(stats.dailyRevenueTrend)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, value]: any) => ({
                date: new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }),
                value: Number(value)
            }))
        : [];

    // Prepare payment type data for pie chart
    const paymentTypeData = stats?.transactionsByPaymentType 
        ? Object.entries(stats.transactionsByPaymentType).map(([name, value]: any) => ({
            name,
            value: Number(value)
        }))
        : [];

    // Prepare transaction type data for bar chart
    const transactionTypeData = stats?.transactionsByType
        ? Object.entries(stats.transactionsByType).map(([name, value]: any) => ({
            name,
            value: Number(value)
        }))
        : [];

    if (isLoading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">{t('Common.loading')}</span>
                        </Spinner>
                    </div>
                </Container>
            </div>
        );
    }

    if (error && !isDemoUser()) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <React.Fragment>
        <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('SalesReports.page_title'), path: "/sale-report"}
                        ]}
                    />

                    <Row className="mb-4">
                        <Col md={3}>
                            <Card>
                                <Card.Body>
                                    <h5>{t('SalesReports.total_sales')}</h5>
                                    <h3>
                                        {isDemoUser() ? '198,000 ₺' : 
                                         stats?.totalRevenue 
                                            ? Number(stats.totalRevenue).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺'
                                            : '0.00 ₺'}
                                    </h3>
                                    {isDemoUser() && <small className="text-success">+12%</small>}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card>
                                <Card.Body>
                                    <h5>{t('SalesReports.total_sale_count')}</h5>
                                    <h3>
                                        {isDemoUser() ? '3,240' : 
                                         stats?.totalTransactions?.toLocaleString('tr-TR') || '0'}
                                    </h3>
                                    {isDemoUser() && <small className="text-danger">-5%</small>}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card>
                                <Card.Body>
                                    <h5>{t('SalesReports.average_order_value')}</h5>
                                    <h3>
                                        {isDemoUser() ? '161.11 ₺' : 
                                         `${averageOrderValue} ₺`}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card>
                                <Card.Body>
                                    <h5>{t('SalesReports.top_store')}</h5>
                                    <h3>
                                        {isDemoUser() ? 'Store 001' : 
                                         topStore ? topStore[0] : 'N/A'}
                                    </h3>
                                    {topStore && (
                                        <small className="text-info">
                                            {Number(topStore[1]).toLocaleString('tr-TR')} {t('SalesReports.transactions')}
                                        </small>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <Card>
                                <Card.Header>📈 {t('SalesReports.sales_over_time')}</Card.Header>
                                <Card.Body>
                                    {isDemoUser() ? (
                                        <IrregularAreaCharts />
                                    ) : dailyRevenueData.length > 0 ? (
                                        <Chart
                                            type="area"
                                            height={350}
                                            series={[{
                                                name: t('SalesReports.revenue'),
                                                data: dailyRevenueData.map(d => d.value)
                                            }]}
                                            options={{
                                                chart: { toolbar: { show: false }, zoom: { enabled: false } },
                                                xaxis: { categories: dailyRevenueData.map(d => d.date) },
                                                stroke: { curve: "smooth" },
                                                colors: ["#0d6efd"],
                                                dataLabels: { enabled: false },
                                                fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 } }
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted text-center py-5">{t('Common.no_data_available')}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Card>
                                <Card.Header>
                                    <h4 className="card-title mb-0">{t('SalesReports.transaction_types')}</h4>
                                </Card.Header>
                                <Card.Body>
                                    {isDemoUser() ? (
                                        <Basicchart dataColors='["--in-success"]'/>
                                    ) : transactionTypeData.length > 0 ? (
                                        <Chart
                                            type="bar"
                                            height={300}
                                            series={[{
                                                name: t('SalesReports.count'),
                                                data: transactionTypeData.map(d => d.value)
                                            }]}
                                            options={{
                                                chart: { toolbar: { show: false } },
                                                xaxis: { categories: transactionTypeData.map(d => d.name) },
                                                colors: ["#198754"],
                                                plotOptions: { bar: { borderRadius: 4 } },
                                                dataLabels: { enabled: false }
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted text-center py-5">{t('Common.no_data_available')}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card>
                                <Card.Header>
                                    <h4 className="card-title mb-0">{t('SalesReports.payment_types')}</h4>
                                </Card.Header>
                                <Card.Body>
                                    {isDemoUser() ? (
                                        <SimplePie dataColors='["--in-primary", "--in-success", "--in-warning", "--in-danger", "--in-info"]'/>
                                    ) : paymentTypeData.length > 0 ? (
                                        <Chart
                                            type="donut"
                                            height={300}
                                            series={paymentTypeData.map(d => d.value)}
                                            options={{
                                                labels: paymentTypeData.map(d => d.name),
                                                colors: ["#0d6efd", "#198754", "#ffc107", "#dc3545", "#0dcaf0"],
                                                legend: { position: "bottom" }
                                            }}
                                        />
                                    ) : (
                                        <p className="text-muted text-center py-5">{t('Common.no_data_available')}</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                </Container>
            </div>

        </React.Fragment>
  )
}

export default SalesReport
